import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset, Placement, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from "@floating-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import InlineSVG from "react-inlinesvg";
import { cn } from "src/lib/cn";

const getAnimationOffset = (placement: Placement) => {
  const side = placement.split("-")[0];
  switch (side) {
    case "bottom":
      return { y: -4 };
    case "top":
      return { y: 4 };
    case "left":
      return { x: 4 };
    case "right":
      return { x: -4 };
    default:
      return { y: -4 };
  }
};

type ISudoTooltipProps = {
  text?: string;
  role?: "tooltip" | "dialog";
  customContent?: React.ReactNode;
  children: React.ReactNode;
  position?: Placement;
  delay?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  onlyShowOnOverflow?: boolean;
  arrowSize?: number;
  arrowColor?: string;
  arrowOffset?: number;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  trigger?: React.ReactNode;
  triggerAs?: React.ElementType;
  tooltipId?: string;
  tooltipClassName?: string;
  tooltipStyle?: React.CSSProperties;
  arrowClassName?: string;
  hasArrow?: boolean;
  closable?: boolean;
  animate?: boolean;
  controlledOpen?: boolean;
  closeButtonClassName?: string;
};

// DEBUG: Set to true to force all tooltips open for inspection
const DEBUG_FORCE_OPEN = false;

export const SudoTooltip = ({
  text,
  role: ariaRole = "dialog",
  customContent,
  children,
  position = "bottom",
  delay = 0,
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  onlyShowOnOverflow = false,
  arrowSize = 20,
  arrowColor = "var(--grey-0)",
  arrowOffset = 0,
  arrowClassName,
  hasArrow = true,
  triggerClassName,
  triggerStyle,
  trigger,
  triggerAs: TriggerComponent = "div",
  tooltipId,
  tooltipClassName,
  tooltipStyle,
  closable = false,
  animate = false,
  closeButtonClassName = "",
}: ISudoTooltipProps) => {
  const [open, setOpen] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const arrowRef = React.useRef(null);
  const triggerRef = React.useRef<Element | null>(null);
  const isControlled = controlledOpen !== undefined;

  const { refs, floatingStyles, context, placement, middlewareData } = useFloating({
    open: isControlled ? controlledOpen : open,
    onOpenChange: (value) => {
      if (!isControlled) {
        setOpen(value);
      }
      onOpenChange?.(value);
    },
    placement: position,
    middleware: [offset(hasArrow ? arrowSize / 2 : 0), flip(), shift({ padding: 5 }), ...(hasArrow ? [arrow({ element: arrowRef })] : [])],
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
  });

  const updateOverflow = React.useCallback(() => {
    if (!onlyShowOnOverflow) return;

    const element = triggerRef.current;
    const nextIsOverflowing = !!element && (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    setIsOverflowing((currentIsOverflowing) => (currentIsOverflowing === nextIsOverflowing ? currentIsOverflowing : nextIsOverflowing));
  }, [onlyShowOnOverflow]);

  const setTriggerReference = React.useCallback(
    (element: Element | null) => {
      triggerRef.current = element;
      refs.setReference(element);
    },
    [refs],
  );

  React.useLayoutEffect(() => {
    if (!onlyShowOnOverflow) return;

    const element = triggerRef.current;
    if (!element) return;

    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(element);

    return () => observer.disconnect();
  }, [onlyShowOnOverflow, updateOverflow]);

  // Calculate arrow positioning with custom offset
  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;

  let finalArrowX = arrowX;
  let finalArrowY = arrowY;

  if (hasArrow && arrowOffset !== undefined) {
    const isVertical = placement.includes("top") || placement.includes("bottom");
    if (isVertical && arrowX !== undefined) {
      finalArrowX = arrowX + arrowOffset;
    } else if (!isVertical && arrowY !== undefined) {
      finalArrowY = arrowY + arrowOffset;
    }
  }

  const hover = useHover(context, {
    delay: {
      open: delay,
      close: 0,
    },
    enabled: !disabled && !isControlled,
  });

  const focus = useFocus(context, {
    enabled: !disabled,
  });

  const dismiss = useDismiss(context, {
    enabled: !isControlled,
  });
  const role = useRole(context, { role: ariaRole });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const showTooltip = DEBUG_FORCE_OPEN || ((isControlled ? controlledOpen : open) && !disabled && (!onlyShowOnOverflow || isOverflowing));

  // Container props for positioning (floating-ui handles this)
  const positioningProps = {
    ref: refs.setFloating,
    id: tooltipId,
    style: { ...floatingStyles, zIndex: 1000000000 },
    ...getFloatingProps(),
  };

  // Content props for styling
  const contentProps: any = {
    className: cn("block max-w-[180px] bg-[var(--grey-0)] rounded-xl text-xs font-normal text-center z-[50000] p-2 shadow-[var(--default-drop-shadow)] cursor-auto h-fit", tooltipClassName),
    style: {
      color: "var(--context-menu-text)",
      ...tooltipStyle,
    },
  };

  // Add animation props if animate is true
  if (animate) {
    contentProps.animate = { y: [-2, 2, -2] };
    contentProps.transition = {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    };
  }

  const ContentContainer = animate ? motion.div : "div";

  return (
    <>
      <TriggerComponent
        ref={setTriggerReference}
        className={`w-fit h-fit ${triggerClassName}`}
        style={triggerStyle}
        {...getReferenceProps({
          onFocus: updateOverflow,
          onMouseEnter: updateOverflow,
        })}
      >
        {trigger || children}
      </TriggerComponent>
      <AnimatePresence>
        {showTooltip && (text?.length || customContent) && (
          <FloatingPortal>
            <div {...positioningProps}>
              <motion.div
                initial={{ opacity: 0, ...getAnimationOffset(placement) }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                exit={{ opacity: 0, ...getAnimationOffset(placement) }}
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <ContentContainer {...contentProps}>
                  {!!closable && (
                    <button
                      title="Close"
                      className={`close absolute flex justify-center items-center p-0 top-1 right-1 z-10 w-4 h-4 bg-transparent border-none cursor-pointer ${closeButtonClassName}`}
                      onClick={() => {
                        if (!isControlled) {
                          setOpen(false);
                        }
                        onOpenChange?.(false);
                      }}
                    >
                      <InlineSVG
                        className="fill-white w-4 h-4"
                        src="/assets/close.svg"
                      />
                    </button>
                  )}
                  {customContent || <div className="tooltip-text">{text}</div>}
                  {hasArrow ? (
                    <FloatingArrow
                      ref={arrowRef}
                      context={context}
                      fill={arrowColor}
                      width={arrowSize}
                      height={arrowSize / 2}
                      className={arrowClassName}
                      style={{
                        ...(finalArrowX !== undefined && { left: finalArrowX }),
                        ...(finalArrowY !== undefined && { top: finalArrowY }),
                      }}
                    />
                  ) : null}
                </ContentContainer>
              </motion.div>
            </div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
};
